import CrossIcon from '../../../../../ui/components/icons/cross-icon';
import TickIcon from '../../../../../ui/components/icons/tick-icon';
import GradientText from '../../../../../ui/flow-components/gradient-text';
import { PackageDisplayConfig } from '../config/package-mapper';
import { BillingFrequency, Feature } from '../config/packages-display.config';
import { GOLD_ACCENT_COLOR, GOLD_BADGE_GRADIENT } from '../config/self-checkout-codes.config';
import { PackageCategory } from '../enums/package-category.enum';

interface TierCardProps {
  packageDisplayConfig: PackageDisplayConfig;
  isSelected: boolean;
  selectedBillingFrequency: BillingFrequency;
  packageCategory: PackageCategory;
  setTier: (packageCategory: PackageCategory) => void;
  /** Optional: durchgestrichener Vergleichspreis (z. B. bei LITE der reguläre Basic-Preis) */
  originalPriceDisplay?: string;
  /** Optional: ersetzt das "Am beliebtesten"-Badge durch eigenen Text (z. B. "LITE-Code aktiv") */
  unlockBadgeText?: string;
  /** Optional: Optik des Unlock-Badges (default `brand`; `gold` für den GOLD-Code) */
  unlockBadgeVariant?: 'brand' | 'gold';
  /** Optional: abweichender Preis für den ersten Monat (z. B. bei aktivem GOLD-Rabatt) */
  firstMonthPriceDisplay?: string;
  /** Optional: override der Breite (default `w-full lg:w-1/2`); für Single-Card-Setups */
  widthClassName?: string;
}

export const TierCard = ({
  packageDisplayConfig,
  isSelected,
  selectedBillingFrequency,
  packageCategory,
  setTier,
  originalPriceDisplay,
  unlockBadgeText,
  unlockBadgeVariant = 'brand',
  firstMonthPriceDisplay,
  widthClassName = 'w-full lg:w-1/2'
}: TierCardProps) => {
  /*const pricingData = packageDisplayConfig.pricing[billingFrequency.toLowerCase() as keyof typeof packageDisplayConfig.pricing];
  if (!pricingData) return null;*/
  const data = packageDisplayConfig[selectedBillingFrequency];

  const shortNameSplit = data.shortName.split(' ');
  const shortNameFirstPart = shortNameSplit.length > 0 ? shortNameSplit[0] : null;
  const shortNameSecondPart = shortNameSplit.length > 1 ? shortNameSplit[1] : null;

  return (
    <div
      onClick={() => setTier(packageCategory)}
      className={`cursor-pointer relative ${widthClassName} flex flex-col transition-all rounded-2xl p-[1.5px] duration-300 select-none`}>
      {isSelected && (
        <div
          className="pointer-events-none absolute inset-0 rounded-2xl p-[1.5px] bg-gradient-to-b from-[#BFD464] to-[#5FD5DE]"
          style={{
            WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude'
          }}
        />
      )}

      {/* Unlock Badge (z. B. LITE-Code aktiv) hat Vorrang vor Most-Popular */}
      {unlockBadgeText ? (
        <div className="absolute top-6 right-6 z-20">
          {unlockBadgeVariant === 'gold' ? (
            <span
              className="text-[#12282A] rounded-full px-3 py-1 text-xs font-medium border border-white/10"
              style={{ background: GOLD_BADGE_GRADIENT }}>
              <span className="w-2 h-2 bg-[#12282A]/40 rounded-full inline-block mr-2"></span>
              {unlockBadgeText}
            </span>
          ) : (
            <span className="bg-[#5FD5DE]/20 text-[#5FD5DE] rounded-full px-3 py-1 text-xs font-medium border border-[#5FD5DE]/20">
              <span className="w-2 h-2 bg-[#5FD5DE] rounded-full inline-block mr-2"></span>
              {unlockBadgeText}
            </span>
          )}
        </div>
      ) : (
        packageDisplayConfig.isMostPopular && (
          <div className="absolute top-6 right-6 z-20">
            <span className="bg-[#BFD464]/20 text-[#BFD464] rounded-full px-3 py-1 text-xs font-medium border border-[#BFD464]/20">
              <span className="w-2 h-2 bg-[#BFD464] rounded-full inline-block mr-2"></span>Am beliebtesten
            </span>
          </div>
        )
      )}

      <div
        className={`relative rounded-2xl transition-shadow duration-200 ${
          isSelected
            ? 'shadow-[inset_0_-16px_36px_rgba(255,255,255,0.1),_0_14px_40px_-12px_rgba(95,213,222,0.5)] bg-white/5'
            : 'hover:shadow-[inset_0_-16px_36px_rgba(255,255,255,0.05)] bg-white/5 border-white/10 border-[1.5px]'
        }`}>
        <div className={`flex flex-col gap-6 p-[32px] rounded-2xl relative transition-all duration-300 ${isSelected ? '' : ''}`}>
          <div className="flex flex-col gap-2 items-start">
            <h2 className="text-lg font-medium text-white/100 tracking-tight mb-1">
              {shortNameFirstPart && shortNameSecondPart ? (
                <>
                  <GradientText shadow="none">{shortNameFirstPart}</GradientText> {shortNameSecondPart}
                </>
              ) : (
                <>{data.shortName}</>
              )}
            </h2>
            {originalPriceDisplay && (
              <span className="text-sm text-[#B2B7B7]/60 line-through font-medium -mb-1">Regulär {originalPriceDisplay}€/Monat</span>
            )}
            <span className="flex items-baseline gap-2">
              {isSelected ? (
                <GradientText className="text-[2.5rem] md:text-[3.25rem] font-medium" shadow="none">
                  {data.price}€
                </GradientText>
              ) : (
                <span className="text-[2.5rem] md:text-[3.25rem] font-medium text-[#e0e0e0] italic font-['LibreCaslonText']">{data.price}€</span>
              )}
              <span className="ml-2 text-sm text-[#B2B7B7] font-medium">pro Monat</span>
            </span>
            {data.slotString && (
              <span className="text-sm text-[#B2B7B7]/60 italic font-medium whitespace-nowrap -mt-1">
                <span className="mr-1">{data.slotString}</span>
                {data.slotStringHighlight && <GradientText shadow="none">{data.slotStringHighlight}</GradientText>}
              </span>
            )}
            {firstMonthPriceDisplay && (
              <span className="text-sm font-medium mt-1" style={{ color: GOLD_ACCENT_COLOR }}>
                - {firstMonthPriceDisplay}€ Rabatt im ersten Monat
              </span>
            )}
          </div>
          <p className="text-[#B2B7B7] text-sm mb-2 -mt-1">{packageDisplayConfig.description}</p>
          {/* Leistungen (features) */}
          <div className="flex flex-col gap-2">
            <span className="text-slate-200 font-medium mb-3 text-sm">
              Enthaltene <GradientText shadow="none">Leistungen</GradientText> ...
            </span>
            <div className="flex flex-col gap-4">
              {packageDisplayConfig.features.map((feature: Feature, index: number) => {
                return (
                  <div
                    key={index}
                    className={`flex items-center gap-2 text-[15px] md:text-base rounded px-2 py-[2px] ${
                      !feature.active ? 'text-[#B2B7B7]/50' : 'text-[#B2B7B7]'
                    } font-[500]`}>
                    {feature.active && <TickIcon size={20} active={true} fill={'#ffffff4d'} stroke={'#ffffff'} />}
                    {!feature.active && <CrossIcon size={20} active={true} fill={'#ffffff12'} stroke={'#ffffff4d'} />}
                    <span className="text-sm">{feature.title}</span>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Bonus */}
          {packageDisplayConfig.bonus && (
            <div className="relative rounded-2xl p-[1.5px]">
              <div
                className="pointer-events-none absolute inset-0 rounded-2xl p-[1.5px] bg-gradient-to-b from-[#BFD464] to-[#5FD5DE]"
                style={{
                  WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude'
                }}
              />
              <div
                className="relative rounded-2xl px-5 py-4 flex flex-col gap-1"
                style={{
                  boxShadow: 'inset 0 -16.5px 36.9px 0 rgba(255, 255, 255, 0.12), 0 14px 40px -12px rgba(95, 213, 222, 0.5)'
                }}>
                <span className="text-lg">
                  <GradientText shadow="none">+ Bonus</GradientText>
                </span>
                <span className="text-[#B2B7B7] text-sm font-medium">{packageDisplayConfig.bonus}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
